/**
 * Theme-aware React Select styles.
 * Uses CSS custom properties so they respond to [data-theme] changes
 * without needing to know the current theme in JS.
 */

export const getSelectStyles = (overrides: Record<string, any> = {}) => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
        ...base,
        minHeight: '44px',
        background: 'var(--surface-input)',
        backgroundColor: 'var(--surface-input)',
        borderColor: state.isFocused ? 'var(--accent)' : 'var(--border)',
        boxShadow: state.isFocused ? '0 0 0 2px var(--accent-subtle)' : 'none',
        '&:hover': { borderColor: 'var(--accent)' },
        ...overrides.control,
    }),
    option: (base: any, state: any) => ({
        ...base,
        background: state.isSelected
            ? 'var(--accent-subtle)'
            : state.isFocused
                ? 'var(--surface-hover)'
                : 'var(--surface-elevated)',
        backgroundColor: state.isSelected
            ? 'var(--accent-subtle)'
            : state.isFocused
                ? 'var(--surface-hover)'
                : 'var(--surface-elevated)',
        // ⛔ isDisabled MUST be handled here. These overrides replace react-select's own option CSS
        // wholesale, so without this branch a disabled option renders in full-strength text with a
        // pointer cursor: it looks clickable, does nothing, and explains nothing. `isOptionDisabled`
        // was already in use on the managed-Postgres size picker and had exactly that bug.
        color: state.isDisabled
            ? 'var(--text-secondary)'
            : state.isSelected ? 'var(--accent)' : 'var(--text-primary)',
        opacity: state.isDisabled ? 0.55 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        ...overrides.option,
    }),
    menu: (base: any) => ({
        ...base,
        background: 'var(--surface-elevated)',
        backgroundColor: 'var(--surface-elevated)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-lg)',
        ...overrides.menu,
    }),
    menuList: (base: any) => ({
        ...base,
        background: 'var(--surface-elevated)',
        backgroundColor: 'var(--surface-elevated)',
        padding: '4px',
        ...overrides.menuList,
    }),
    singleValue: (base: any) => ({
        ...base,
        color: 'var(--text-primary)',
        ...overrides.singleValue,
    }),
    placeholder: (base: any) => ({
        ...base,
        color: 'var(--text-placeholder)',
        ...overrides.placeholder,
    }),
    input: (base: any) => ({
        ...base,
        color: 'var(--text-primary)',
        ...overrides.input,
    }),
    indicatorSeparator: (base: any) => ({
        ...base,
        background: 'var(--border)',
        backgroundColor: 'var(--border)',
        ...overrides.indicatorSeparator,
    }),
    dropdownIndicator: (base: any) => ({
        ...base,
        color: 'var(--text-muted)',
        '&:hover': { color: 'var(--text-primary)' },
        ...overrides.dropdownIndicator,
    }),
    clearIndicator: (base: any) => ({
        ...base,
        color: 'var(--text-muted)',
        '&:hover': { color: 'var(--text-primary)' },
        ...overrides.clearIndicator,
    }),
    multiValue: (base: any) => ({
        ...base,
        background: 'var(--surface-hover)',
        backgroundColor: 'var(--surface-hover)',
        ...overrides.multiValue,
    }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: 'var(--text-primary)',
        ...overrides.multiValueLabel,
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: 'var(--text-muted)',
        '&:hover': { background: 'var(--border)', color: 'var(--text-primary)' },
        ...overrides.multiValueRemove,
    }),
    valueContainer: (base: any) => ({
        ...base,
        ...overrides.valueContainer,
    }),
    // A grouped menu (the free VM rows' locked actions) reads in the theme, not react-select's grey capitals.
    groupHeading: (base: any) => ({
        ...base,
        color: 'var(--text-secondary)',
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
        ...overrides.groupHeading,
    }),
});

/** Compact variant for action dropdowns (no border when not focused) */
export const getActionSelectStyles = () => getSelectStyles({
    control: {
        minHeight: '36px',
        fontSize: '0.875rem',
    },
});
